import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { isAfter } from 'date-fns';
import { FaTrashAlt } from 'react-icons/fa';
import { TwIconButton, FormRow } from 'components/common';

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const errClass = 'tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0';

const BookingMultipleStopTripForm = React.memo(({
  index, transit, control, register, errors, setValue, getValues, removeTransit, updatedTransit,
}) => {
  useEffect(() => {
    if (transit) {
      Object.entries(transit).forEach(([key, value]) => {
        if ((key === 'from_date' || key === 'to_date')) {
          if (value) setValue(`${key}_${index}`, new Date(value));
        } else if (['from_location', 'to_location', 'description'].includes(key)) {
          setValue(`${key}_${index}`, value);
        }
      });
    }
  }, [transit, index, setValue]);

  const handleFieldChange = (fieldName, value) => updatedTransit(index, { ...transit, [fieldName]: value });

  const fromDate = `from_date_${index}`;
  const toDate = `to_date_${index}`;
  const fromLoc = `from_location_${index}`;
  const toLoc = `to_location_${index}`;
  const desc = `description_${index}`;

  return (
    <div className="tw-mb-4 tw-pb-4 tw-border-b tw-border-dashed tw-border-slate-200">
      <div className="tw-flex tw-justify-end tw-mb-2">
        <TwIconButton variant="secondary" size="sm" icon={FaTrashAlt} onClick={removeTransit} title="Remove" />
      </div>

      <FormRow label="Trip date">
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex-1">
            <Controller control={control} name={fromDate} rules={{ required: 'From date must be required.' }}
              render={({ field }) => (
                <DatePicker selected={field.value} className={inputClass} placeholderText="DD-MM-YYYY h:mm aa"
                  timeIntervals={5} dateFormat="dd-MM-yyyy h:mm aa" showTimeSelect fixedHeight
                  onChange={(date) => { field.onChange(date); handleFieldChange('from_date', date); }} />
              )} />
            {errors[fromDate] && <p className={errClass}>{errors[fromDate].message}</p>}
          </div>
          <span className="tw-text-slate-500">to</span>
          <div className="tw-flex-1">
            <Controller control={control} name={toDate}
              rules={{ required: 'To date must be required.', validate: { afterFromDate: (v) => isAfter(v, getValues(fromDate)) || 'To date must be after from date.' } }}
              render={({ field }) => (
                <DatePicker selected={field.value} className={inputClass} placeholderText="DD-MM-YYYY h:mm aa"
                  timeIntervals={5} dateFormat="dd-MM-yyyy h:mm aa" showTimeSelect fixedHeight
                  onChange={(date) => { field.onChange(date); handleFieldChange('to_date', date); }} />
              )} />
            {errors[toDate] && <p className={errClass}>{errors[toDate].message}</p>}
          </div>
        </div>
      </FormRow>

      <FormRow label="Location">
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex-1">
            <input placeholder="From" className={inputClass}
              {...register(fromLoc, { required: 'From location must be required.' })}
              onChange={(e) => { setValue(fromLoc, e.target.value); handleFieldChange('from_location', e.target.value); }} />
            {errors[fromLoc] && <p className={errClass}>{errors[fromLoc].message}</p>}
          </div>
          <span className="tw-text-slate-500">to</span>
          <div className="tw-flex-1">
            <input placeholder="To" className={inputClass}
              {...register(toLoc, { required: 'To location must be required.' })}
              onChange={(e) => { setValue(toLoc, e.target.value); handleFieldChange('to_location', e.target.value); }} />
            {errors[toLoc] && <p className={errClass}>{errors[toLoc].message}</p>}
          </div>
        </div>
      </FormRow>

      <FormRow label="Description">
        <textarea rows={5} placeholder="Description" className={inputClass}
          {...register(desc, { required: 'Description must be required.' })}
          onChange={(e) => { setValue(desc, e.target.value); handleFieldChange('description', e.target.value); }} />
        {errors[desc] && <p className={errClass}>{errors[desc].message}</p>}
      </FormRow>
    </div>
  );
});

BookingMultipleStopTripForm.displayName = 'BookingMultipleStopTripForm';

BookingMultipleStopTripForm.propTypes = {
  index: PropTypes.number.isRequired,
  transit: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  removeTransit: PropTypes.func.isRequired,
  updatedTransit: PropTypes.func.isRequired,
};

export default BookingMultipleStopTripForm;
